import React, { useMemo, useState } from "react";
import type { EmissionTarget } from "api/emission-targets";
import { CustomMaterialTable, DeleteDialog, Loading } from "components";
import { useEmissionTargets, useDeleteEmissionTarget, useNotify } from "hooks";
import { getTargetsTableColumns } from "./getTargetsTableColumns";

export interface TargetsSettingsTableProps {
  onEditTarget: (target: EmissionTarget) => void;
}

export const TargetsSettingsTable = ({ onEditTarget }: TargetsSettingsTableProps) => {
  const notify = useNotify();
  const { data: targets = [], isLoading } = useEmissionTargets();
  const { mutateAsync: deleteTarget } = useDeleteEmissionTarget();
  const [targetToDelete, setTargetToDelete] = useState<EmissionTarget | null>(null);

  const columns = useMemo(
    () =>
      getTargetsTableColumns(onEditTarget, (target) => setTargetToDelete(target)),
    [onEditTarget]
  );

  const handleDeleteConfirm = async () => {
    if (!targetToDelete) return;
    try {
      await deleteTarget(targetToDelete.id);
      notify.success("Target deleted successfully.");
      setTargetToDelete(null);
    } catch (err: unknown) {
      const message =
        typeof (err as { response?: { data?: string } })?.response?.data === "string"
          ? (err as { response: { data: string } }).response.data
          : "Failed to delete target.";
      notify.error(message);
    }
  };

  const handleDeleteCancel = () => {
    setTargetToDelete(null);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <CustomMaterialTable<EmissionTarget>
        columns={columns}
        data={targets}
        enablePagination={false}
        enableColumnResizing
        defaultColumn={{ filterFn: "startsWith" }}
        containerSx={{
          height: "calc(100vh - 260px)",
          width: "calc(100vw - 150px)",
          minHeight: "200px",
          "& tbody tr td:first-of-type": {
            paddingLeft: "10px",
          }
        }}
      />
      {targetToDelete && (
        <DeleteDialog
          isOpen={!!targetToDelete}
          onCancel={handleDeleteCancel}
          onDeleteClick={handleDeleteConfirm}
          target="target"
          title="Delete Target?"
        />
      )}
    </>
  );
};
